import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";

function useWindowWidth() {
  const [width, setWidth] = useState(() => (
    typeof window !== "undefined" ? window.innerWidth : 1024
  ));
  const handleResize = useCallback(() => setWidth(window.innerWidth), []);
  useEffect(() => {
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);
  return width;
}

export function GalleryScreen() {
  const [activeTab, setActiveTab] = useState("all"); // all, ai, uploaded
  const [aiDesigns, setAiDesigns] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const windowWidth = useWindowWidth();

  useEffect(() => {
    loadGallery();
  }, []);

  async function loadGallery() {
    setLoading(true);
    try {
      // Load AI-generated designs
      const aiRes = await fetch("/api/public/gallery?limit=60");
      const aiData = await aiRes.json();
      setAiDesigns(aiData.designs || []);

      // Load technician uploads
      const uploadRes = await fetch("/api/gallery?type=uploaded&limit=60");
      const uploadData = await uploadRes.json();
      setUploads(uploadData.uploads || []);
    } catch (err) {
      console.error("Gallery load error:", err);
    }
    setLoading(false);
  }

  const allItems = [
    ...uploads.map(u => ({ ...u, type: "uploaded" })),
    ...aiDesigns.map(d => ({ ...d, type: "ai" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const displayItems = activeTab === "all" ? allItems :
                       activeTab === "ai" ? aiDesigns :
                       uploads;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div className="gallery-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader
          emoji="🖼️"
          title="Design gallery"
          subtitle="AI-crafted designs and real nail art from our talented technicians"
        />

        {/* Tab Navigation */}
        <div className="gallery-tabs" style={{
          display: "flex",
          gap: windowWidth < 640 ? 6 : 8,
          marginBottom: 24,
          borderBottom: "1px solid var(--border)",
          paddingBottom: 2,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}>
          <TabButton
            active={activeTab === "all"}
            onClick={() => setActiveTab("all")}
            label="All"
            count={allItems.length}
            compact={windowWidth < 640}
          />
          <TabButton
            active={activeTab === "ai"}
            onClick={() => setActiveTab("ai")}
            label="AI Generated"
            count={aiDesigns.length}
            icon="✨"
            compact={windowWidth < 640}
          />
          <TabButton
            active={activeTab === "uploaded"}
            onClick={() => setActiveTab("uploaded")}
            label="Real Work"
            count={uploads.length}
            icon="💅"
            compact={windowWidth < 640}
          />
        </div>

        {loading ? (
          <Card>
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div className="spinner" style={{ margin: "0 auto 16px" }} />
              <div style={{ color: "var(--text-muted)" }}>Loading gallery...</div>
            </div>
          </Card>
        ) : displayItems.length === 0 ? (
          <Card>
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎨</div>
              <div style={{ fontSize: 16, marginBottom: 8 }}>No designs yet</div>
              <div style={{ fontSize: 14 }}>
                {activeTab === "uploaded"
                  ? "Technicians haven't uploaded any work yet"
                  : "The gallery fills up as customers finalize designs"}
              </div>
            </div>
          </Card>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: windowWidth < 640
              ? "repeat(2, minmax(0, 1fr))"
              : windowWidth < 1024
                ? "repeat(2, minmax(0, 1fr))"
                : "repeat(3, minmax(0, 1fr))",
            gap: windowWidth < 640 ? 12 : 16,
          }}>
            {displayItems.map((item, idx) => (
              <GalleryItem
                key={item.id}
                item={item}
                index={idx}
                onClick={() => setSelectedImage(item)}
              />
            ))}
          </div>
        )}

        {!loading && displayItems.length > 0 && (
          <div style={{ textAlign: "center", marginTop: 48 }}>
            <Link
              to="/signup"
              style={{
                display: "inline-block",
                padding: "14px 32px",
                borderRadius: 999,
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                color: "#fff",
                fontWeight: 600,
                textDecoration: "none",
                fontSize: 15,
                boxShadow: "0 4px 14px rgba(146, 104, 182, 0.3)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(146, 104, 182, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 14px rgba(146, 104, 182, 0.3)";
              }}
            >
              Create your own design →
            </Link>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, label, count, icon, compact = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: compact ? "10px 10px" : "10px 18px",
        border: "none",
        background: "transparent",
        color: active ? "var(--mauve)" : "var(--text-muted)",
        fontWeight: active ? 600 : 500,
        fontSize: compact ? 13 : 14,
        cursor: "pointer",
        borderBottom: active ? "2px solid var(--mauve)" : "2px solid transparent",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        gap: 6,
        flex: "0 0 auto",
        whiteSpace: "nowrap",
      }}
    >
      {icon && <span>{icon}</span>}
      <span>{label}</span>
      <span style={{
        fontSize: 12,
        background: active ? "var(--mauve)" : "var(--bg-elevated)",
        color: active ? "#fff" : "var(--text-muted)",
        padding: compact ? "2px 7px" : "2px 8px",
        borderRadius: 999,
        fontWeight: 600,
        flexShrink: 0,
      }}>
        {count}
      </span>
    </button>
  );
}

function GalleryItem({ item, index, onClick }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const isUpload = item.type === "uploaded";

  return (
    <article
      className="gallery-item"
      onClick={onClick}
      style={{
        background: "var(--bg-elevated)",
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid var(--border)",
        cursor: "pointer",
        transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s",
        animation: `fadeInUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.05}s both`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
        e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ position: "relative" }}>
        <img
          src={item.thumbnail_url || item.image_url}
          alt={item.name || item.description || "Gallery image"}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          style={{
            width: "100%",
            aspectRatio: "1/1",
            objectFit: "cover",
            display: "block",
            opacity: isLoaded ? 1 : 0,
            transition: "opacity 0.4s",
          }}
        />
        {!isLoaded && (
          <div style={{
            position: "absolute",
            inset: 0,
            background: "var(--bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <div className="spinner" />
          </div>
        )}
        {isUpload && (
          <div style={{
            position: "absolute",
            top: 10,
            right: 10,
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(8px)",
            color: "#fff",
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            <span>💅</span>
            <span>Real Work</span>
          </div>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{
          fontFamily: "var(--font-display)",
          fontSize: 15,
          fontWeight: 700,
          color: "var(--text)",
          marginBottom: 4,
        }}>
          {item.name || "Untitled"}
        </div>
        {item.tagline && (
          <div style={{
            fontSize: 12,
            color: "var(--text-muted)",
            fontStyle: "italic",
            lineHeight: 1.4,
          }}>
            {item.tagline}
          </div>
        )}
        {item.description && (
          <div style={{
            fontSize: 12,
            color: "var(--text-muted)",
            marginTop: 6,
            lineHeight: 1.5,
          }}>
            {item.description.length > 80
              ? item.description.substring(0, 80) + "..."
              : item.description}
          </div>
        )}
        {item.uploader_name && (
          <div style={{
            fontSize: 11,
            color: "var(--mauve)",
            marginTop: 8,
            fontWeight: 500,
          }}>
            by {item.uploader_name}
          </div>
        )}
      </div>
    </article>
  );
}

function ImageModal({ image, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "fadeIn 0.3s",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "rgba(255,255,255,0.2)",
          backdropFilter: "blur(10px)",
          border: "none",
          color: "#fff",
          width: 40,
          height: 40,
          borderRadius: "50%",
          fontSize: 24,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        ×
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 900,
          maxHeight: "90vh",
          background: "var(--bg-elevated)",
          borderRadius: 20,
          overflow: "hidden",
          animation: "scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        <img
          src={image.image_url}
          alt={image.name || image.description}
          style={{
            width: "100%",
            maxHeight: "70vh",
            objectFit: "contain",
            display: "block",
          }}
        />
        <div style={{ padding: 24 }}>
          <h3 style={{
            fontFamily: "var(--font-display)",
            fontSize: 24,
            fontWeight: 700,
            color: "var(--text)",
            marginBottom: 8,
          }}>
            {image.name || "Design"}
          </h3>
          {image.tagline && (
            <p style={{
              fontSize: 14,
              color: "var(--text-muted)",
              fontStyle: "italic",
              marginBottom: 12,
            }}>
              {image.tagline}
            </p>
          )}
          {image.description && (
            <p style={{
              fontSize: 14,
              color: "var(--text-soft)",
              lineHeight: 1.6,
              marginBottom: 12,
            }}>
              {image.description}
            </p>
          )}
          {image.uploader_name && (
            <p style={{
              fontSize: 13,
              color: "var(--mauve)",
              fontWeight: 500,
            }}>
              Created by {image.uploader_name}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
